import './../../config/polyfills';

import * as React from 'react';
import { render, hydrate } from 'react-dom';
import { BrowserRouter as Router } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Hydrate } from 'react-query/hydration';
import analytics from 'storefront-analytics/dist/analytics';
import {
  ChirpThemeProvider,
  STATIC_ENV_URLS as CHIRP_THEME_STATIC_ENV_URLS,
} from '@chewy/chirp-theme-provider';
import { SpaClientHeader } from '@chewy/header-footer-react/dist/components/react/src/header/spa/spa-header';
import { SpaClientFooter } from '@chewy/header-footer-react/dist/components/react/src/footer/spa/spa-footer';
import { SpaHeaderContext } from '@chewy/header-footer-react/dist/components/react/src/header/spa/spa-header.context';
import { SpaFooterContext } from '@chewy/header-footer-react/dist/components/react/src/footer/spa/spa-footer.context';
import getSpaHeaderContext from 'context/SpaHeaderContext';
import getSpaFooterContext from 'context/SpaFooterContext';
import { AnalyticsProvider } from '../shared/AnalyticsProvider';
import I18nextProvider from '../shared/i18n/I18nextProvider';
import App from '../shared/App';
import Error500 from '../shared/pages/Error500';
import { clientConfig } from '../shared/util/reactQueryOptions';
import { isNotChewyMobileApp } from '../shared/util/helpers';

const renderFn = process.env.REACT_APP_SSR_ENABLED === 'false' ? render : hydrate;

const dehydratedState = window.__REACT_QUERY_STATE__;
const serverError = !!window?.__SERVER_ERROR__;
const queryClient = new QueryClient(clientConfig);
const shouldRenderHeaderAndFooter = !!window?.__RENDER_HEADER_AND_FOOTER__;
const shouldUseMinimalHeaderFooter = !!window?.__USES_MINIMAL_HEADER__;
const isChewyPortalEnabled = !!window?.isChewyPortalEnabled;

const appEnv = process.env.REACT_APP_ENV === 'local' ? 'dev' : process.env.REACT_APP_ENV;
const chirpThemeEnv = appEnv.toUpperCase() as keyof typeof CHIRP_THEME_STATIC_ENV_URLS;

function setViewportProperty(doc: HTMLElement) {
  let prevClientHeight: number;
  function handleResize() {
    const clientHeight = doc.clientHeight;
    if (clientHeight === prevClientHeight) return;
    requestAnimationFrame(function updateViewportHeight() {
      doc.style.setProperty('--vh', clientHeight * 0.01 + 'px');
      prevClientHeight = clientHeight;
    });
  }
  handleResize();
  return handleResize;
}
window.addEventListener('resize', setViewportProperty(document.documentElement));

renderFn(
  <QueryClientProvider client={queryClient}>
    <Hydrate state={dehydratedState}>
      <Router>
        <I18nextProvider>
          <HelmetProvider>
            <ChirpThemeProvider env={chirpThemeEnv} inWebView={!isNotChewyMobileApp()} />
            <AnalyticsProvider analytics={analytics}>
              {isChewyPortalEnabled && (
                <div
                  data-testid="header"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: `<chewy-header data-eyebrow="true" data-version=""></chewy-header>`,
                  }}
                />
              )}
              {!isChewyPortalEnabled && shouldRenderHeaderAndFooter ? (
                <SpaHeaderContext.Provider
                  value={getSpaHeaderContext(shouldUseMinimalHeaderFooter, window.userContextData)}
                >
                  <SpaClientHeader />
                </SpaHeaderContext.Provider>
              ) : null}
              {serverError ? <Error500 /> : <App />}

              {isChewyPortalEnabled && (
                <div
                  data-testid="footer"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: `<chewy-footer></chewy-footer>`,
                  }}
                />
              )}
              {!isChewyPortalEnabled && shouldRenderHeaderAndFooter ? (
                <SpaFooterContext.Provider
                  value={getSpaFooterContext(shouldUseMinimalHeaderFooter, window.userContextData)}
                >
                  <SpaClientFooter />
                </SpaFooterContext.Provider>
              ) : null}
            </AnalyticsProvider>
          </HelmetProvider>
        </I18nextProvider>
      </Router>
    </Hydrate>
  </QueryClientProvider>,
  document.querySelector('#app'),
);

if (process.env.NODE_ENV === 'development') {
  if (module.hot) {
    module.hot.accept();
  }
}
